import { useNode } from '@craftjs/core';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  FormLabel,
  Stack,
  Button,
  Box,
  IconButton,
  Select,
  MenuItem
} from '@mui/material';
import { MuiColorInput } from 'mui-color-input';

interface CTableProps {
  rows?: Array<{ [key: string]: string }>;
  columns?: Array<{ [key: string]: string }>;
  borderColor?: string;
  headerBackgroundColor?: string;
  headerColor?: any;
  contentColor?: any;
  headerFontSize?: any;
  contentFontSize?: any;
  headerFontFamily?: any,
  contentFontFamily?: any;
}

export const CTable = ({
  rows = [],
  columns = [],
  borderColor,
  headerBackgroundColor,
  headerColor,
  contentColor,
  headerFontSize,
  contentFontSize,
  headerFontFamily,
  contentFontFamily,
}: CTableProps) => {
  const {
    connectors: { connect, drag },
    selected
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  return (
    <TableContainer
      component={Paper}
      ref={(ref: any) => connect(drag(ref))}
      className='craft-table'
      sx={{
        borderColor: selected ? '#008522 !important' : '#F0F0F0 !important',
        minHeight: '100px',
        borderRadius: 0,
        boxShadow: 'none',
      }}
    >
      <Table>
        <TableHead style={{ backgroundColor: headerBackgroundColor }}>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell 
                color={headerColor} 
                // @ts-ignore
                align={column.align ?? 'left'} 
                key={index}
                sx={{ padding: '10px' }}
              >
                <span style={{
                  fontSize: headerFontSize, 
                  fontWeight: 600, 
                  color: headerColor,
                  fontFamily: headerFontFamily,
                }} dangerouslySetInnerHTML={{ __html: column.name }} >
                </span>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {columns.map((column, colIndex) => (
                <TableCell 
                  // @ts-ignore
                  align={column.align ?? 'left'} 
                  key={colIndex}
                  sx={{ borderLeft: 'none', borderRight: 'none',  borderBottom: `2px solid ${borderColor} `, padding: '10px' }}
                >
                  <span style={{
                    fontSize: contentFontSize, 
                    color: contentColor,
                    fontFamily: contentFontFamily,
                  }} dangerouslySetInnerHTML={{ __html: row[column.name] }}>
                  </span>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const CTableSettings = () => {
  const {
    actions: { setProp },
    rows,
    columns,
    borderColor,
    headerBackgroundColor,
    headerColor,
    contentColor,
    headerFontSize,
    contentFontSize,
    headerFontFamily,
    contentFontFamily,
  } = useNode((node) => ({
    rows: node.data.props.rows,
    columns: node.data.props.columns,
    borderColor: node.data.props.borderColor,
    headerBackgroundColor: node.data.props.headerBackgroundColor,
    headerColor: node.data.props.headerColor,
    contentColor: node.data.props.contentColor,
    headerFontSize: node.data.props.headerFontSize,
    contentFontSize: node.data.props.contentFontSize,
    headerFontFamily: node.data.props.headerFontFamily,
    contentFontFamily: node.data.props.contentFontFamily,
  }));

  const addColumn = () => {
    setProp((props: any) => props.columns.push({ name: 'New Column', align: 'left' }));
  };

  const deleteColumn = (index: number) => {
    setProp((props: any) => {
      props.columns.splice(index, 1);
      props.rows = props.rows.map((row: any) => {
        const newRow = { ...row };
        delete newRow[props.columns[index]];
        return newRow;
      });
    });
  };

  const addRow = () => {
    setProp((props: any) =>
      props.rows.push(
        Object.fromEntries(props.columns.map((col: any) => [col, '']))
      )
    );
  };

  const deleteRow = (rowIndex: number) => {
    setProp((props: any) => {
      props.rows.splice(rowIndex, 1);
    });
  };

  const handleInputChange = (rowIndex: number, columnName: string, value: string) => {
    setProp((props: any) => {
      props.rows[rowIndex][columnName] = value;
    });
  };

  return (
    <Stack spacing={4} paddingBottom={4}>
      <FormControl size="small" component="fieldset">
        <FormLabel component="legend">Columns</FormLabel>
        <Stack spacing={1} >
          {columns.map((column: any, index: number) => (
            <Stack key={index} direction='row'>
              <TextField
                value={column.name}
                onChange={(e) =>
                  setProp((props: any) => (props.columns[index].name = e.target.value))
                }
                variant="outlined"
                size="small"
                style={{flexGrow: 1}}
              />
              <Select
                value={column.align}
                onChange={(e) =>
                  setProp((props: any) => (props.columns[index].align = e.target.value))
                }
                variant="outlined"
                size="small"
                style={{ marginLeft: '8px', marginRight: '8px' }}
              >
                <MenuItem value="left">Left</MenuItem>
                <MenuItem value="center">Center</MenuItem>
                <MenuItem value="right">Right</MenuItem>
              </Select>
              <IconButton onClick={() => deleteColumn(index)} color="secondary">
                <DeleteIcon />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        <Stack mt={2}>
          <Button variant="contained" color="primary" onClick={addColumn}>
            Add Column
          </Button>
        </Stack>
      </FormControl>
      <FormControl size="small" component="fieldset">
        <Box mb={2}>
          <FormLabel component="legend">Rows</FormLabel>
        </Box>
        {rows.map((row: any, rowIndex: number) => (
          <Box key={rowIndex} sx={{ marginBottom: 2 }}>
            {columns.map((column: any, colIndex: any) => (
              <TextField
                key={colIndex}
                label={column.name}
                value={row[column.name] || ''}
                onChange={(e) =>
                  handleInputChange(rowIndex, column.name, e.target.value)
                }
                variant="outlined"
                size="small"
                style={{ marginRight: '8px', marginBottom: '8px' }}
              />
            ))}
            <IconButton onClick={() => deleteRow(rowIndex)} color="secondary">
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        <Button variant="contained" color="primary" onClick={addRow}>
          Add Row
        </Button>
      </FormControl>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">Border Color</FormLabel>
          <MuiColorInput format='hex' value={borderColor} onChange={(color) => {
              setProp((props: any) => (props.borderColor = color), 500);
            }}
          />
        </FormControl>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">Header Background Color</FormLabel>
          <MuiColorInput format='hex' value={headerBackgroundColor} onChange={(color) => {
              setProp((props: any) => (props.headerBackgroundColor = color), 500);
            }}
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">Header Color</FormLabel>
          <MuiColorInput format='hex' value={headerColor} onChange={(color) => {
              setProp((props: any) => (props.headerColor = color), 500);
            }}
          />
        </FormControl>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">ContentColor</FormLabel>
          <MuiColorInput format='hex' value={contentColor} onChange={(color) => {
              setProp((props: any) => (props.contentColor = color), 500);
            }}
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">Header Font Size</FormLabel>
          <TextField
            value={headerFontSize}
            onChange={(e) =>
              setProp((props: any) => (props.headerFontSize = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
        <FormControl fullWidth={true} component="fieldset">
          <FormLabel component="legend">Content Font Size</FormLabel>
          <TextField
            value={contentFontSize}
            onChange={(e) =>
              setProp((props: any) => (props.contentFontSize = e.target.value))
            }
            variant="outlined"
            size="small"
          />
        </FormControl>
      </Stack>
      <Stack direction='row' spacing={4}>
        <FormControl fullWidth={true} variant="outlined">
          <FormLabel component="legend">Font Family</FormLabel>
          <Select
            value={headerFontFamily}
            onChange={(e) => setProp((props: any) => (props.headerFontFamily = e.target.value))}
          >
            <MenuItem value="Open Sans,sans-serif">Open Sans</MenuItem>
            <MenuItem value="Arial, sans-serif">Arial</MenuItem>
            <MenuItem value="'Courier New', monospace">Courier New</MenuItem>
            <MenuItem value="'Georgia', serif">Georgia</MenuItem>
            <MenuItem value="'Times New Roman', Times, serif">Times New Roman</MenuItem>
            <MenuItem value="'Trebuchet MS', sans-serif">Trebuchet MS</MenuItem>
            <MenuItem value="'Verdana', sans-serif">Verdana</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth={true} variant="outlined">
          <FormLabel component="legend">Font Family</FormLabel>
          <Select
            value={contentFontFamily}
            onChange={(e) => setProp((props: any) => (props.contentFontFamily = e.target.value))}
          >
            <MenuItem value="Open Sans,sans-serif">Open Sans</MenuItem>
            <MenuItem value="Arial, sans-serif">Arial</MenuItem>
            <MenuItem value="'Courier New', monospace">Courier New</MenuItem>
            <MenuItem value="'Georgia', serif">Georgia</MenuItem>
            <MenuItem value="'Times New Roman', Times, serif">Times New Roman</MenuItem>
            <MenuItem value="'Trebuchet MS', sans-serif">Trebuchet MS</MenuItem>
            <MenuItem value="'Verdana', sans-serif">Verdana</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );
};

export const CTableDefaultProps = {
  rows: [],
  columns: [],
  borderColor: '#d5d5d5',
  headerBackgroundColor: '#008522',
  headerColor: '#FFFFFF',
  contentColor: '#000000',
  headerFontSize: '14px',
  contentFontSize: '14px',
  // headerFontFamily: 'Open Sans,sans-serif',
  // contentFontFamily: 'Open Sans,sans-serif',
};

CTable.craft = {
  displayName: "Table",
  props: CTableDefaultProps,
  related: {
    settings: CTableSettings,
  },
};
